import { Link } from "react-router-dom";

import type { WalletTier } from "../api/types";
import { shortId } from "../lib/format";
import { hasRealName, profileURL } from "../lib/polymarket";
import { tierColor, tierLabel } from "../lib/tier";

/**
 * Значок квалификации кошелька.
 *
 * Форма — в классе `.tier` (index.css), цвет — из lib/tier.ts. Разделение не
 * косметическое: какой тир «громче» решается в одном месте вместе с порядком
 * RANK, а тут остаётся только показать.
 */
export function Tier({
  tier,
  title,
  hideEmpty,
}: {
  tier: WalletTier | undefined;
  title?: string;
  /**
   * Не рисовать значок вовсе, когда тира нет.
   *
   * Нужно там, где НЕИЗВЕСТНЫ почти все, — в панели участников рынка. Тир
   * считается по НАШИМ встречам (ADR-0007), а участников короткого окна сотни, и
   * мы с ними в основном не торговали: до 2026-08-12 панель рисовала «неизвестен»
   * в каждой строке, то есть значок сообщал ровно ноль и при этом занимал место
   * рядом с адресом.
   *
   * По умолчанию ложь, и это не мелочь: в ленте контрагент — наш, и там «мы его
   * ещё не профилировали» настоящий ответ, отличный от «профиля нет вовсе».
   */
  hideEmpty?: boolean;
}) {
  if (!tier && hideEmpty) return null;
  return (
    <span className="tier" style={{ color: tierColor(tier) }} title={title}>
      {tierLabel(tier)}
    </span>
  );
}

/**
 * Профиль кошелька НА БИРЖЕ — внешняя ссылка рядом с нашей внутренней.
 *
 * Две ссылки на один адрес не дублируют друг друга: наша страница отвечает «как
 * он торговал ПРОТИВ НАС» (метрики по нашим встречам плюс тир), а биржа знает
 * то, чего у нас нет вовсе, — его позиции и историю по всем рынкам. Разбирая
 * контрагента, оператор ходит именно туда, и адрес до сих пор приходилось
 * копировать руками.
 *
 * В новой вкладке и с `rel`: терминал держит сокет, и уход страницы стоил бы
 * переподключения и повторной гидрации ордеров.
 *
 * `stopPropagation` обязателен: в истории сделок строка нажимается целиком —
 * раскрывает встречные ордера, — и без него переход утаскивал бы за собой
 * сворачивание того, ради чего ссылку и открыли.
 */
export function PolymarketProfile({ address }: { address: string }) {
  const href = profileURL(address);
  // Ссылки нет — значит, адрес неполон (см. lib/polymarket.ts). Внутренняя
  // страница кошелька рядом остаётся, и терять её из-за отсутствия внешней
  // незачем.
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => e.stopPropagation()}
      title="профиль кошелька на Polymarket: его позиции и история по всем рынкам"
      className="text-[10px] text-[var(--color-muted)] hover:text-[var(--color-accent)]"
    >
      PM ↗
    </a>
  );
}

/**
 * Имя кошелька ссылкой на его профиль НА БИРЖЕ.
 *
 * Ссылка ведёт по АДРЕСУ, а не по имени: биржа знает участника по proxy-кошельку,
 * а имя — подпись, которую он может сменить. Строится через lib/polymarket.ts,
 * потому что адрес приходит с провода: подставлять непроверенное в href значило
 * бы уводить оператора по ссылке, собранной из чужих данных. Не построилась —
 * остаётся текст, и это правильнее пустого места.
 *
 * Настоящая ссылка, а не onClick: средняя кнопка мыши, «открыть в новой вкладке»
 * и «копировать» должны работать как везде. В новой вкладке и с `rel` — терминал
 * держит сокет, и уход страницы стоил бы переподключения и повторной гидрации
 * ордеров (то же правило, что у PolymarketProfile). `stopPropagation` — потому
 * что строку таблицы нажимают целиком: в своде встреч это раскрывает сделки, и
 * без него переход утаскивал бы за собой сворачивание.
 *
 * Рядом с адресом, который ведёт на НАШУ страницу кошелька, это не дублирование:
 * наша отвечает «как он торговал против нас», биржа знает то, чего у нас нет
 * вовсе, — его позиции и историю по всем рынкам.
 *
 * Ничего не рисует без имени: имени нет у 12 % кошельков по замеру, и выдумывать
 * там нечего.
 */
export function ProfileName({ wallet, name }: { wallet: string; name: string }) {
  if (!name) return null;
  const href = profileURL(wallet);
  if (!href) return <span title={name}>{name}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => e.stopPropagation()}
      title={`${name}\nпрофиль на Polymarket: позиции и история по всем рынкам`}
      className="hover:text-[var(--color-accent)] hover:underline"
    >
      {name}
    </a>
  );
}

/**
 * Кошелёк ОДНОЙ подписью: имя, а у неподписанных — адрес.
 *
 * # Почему не две колонки
 *
 * Потому что имя и адрес — один и тот же ответ на один и тот же вопрос «кто
 * это», и рядом они стояли избыточно: у 88 % кошельков есть имя, и адрес возле
 * него не читают вовсе, а у остальных пустая клетка занимала место ради
 * пустоты. Одна подпись, которую глаз просматривает сверху вниз, отвечает лучше
 * двух.
 *
 * # Куда ведёт ссылка и почему их всё-таки две
 *
 * Сама подпись — на НАШУ страницу кошелька (как он торговал против нас), а «PM ↗»
 * рядом — на профиль биржи (его позиции и история по всем рынкам). Это разные
 * ответы, и до слияния колонок их держали разные ссылки: адрес вёл к нам, имя —
 * на биржу. Потерять вторую значило бы отобрать у оператора то, ради чего он в
 * панель и приходит, поэтому она осталась значком.
 *
 * Адрес при этом никуда не делся: он в подсказке — и у подписанных, где на
 * экране его нет, он единственный способ узнать, ТОТ ЛИ это кошелёк (имя на
 * бирже меняется одним нажатием и не уникально).
 */
export function WalletLabel({ address, name }: { address: string; name: string }) {
  // Псевдоним биржи именем не считается: см. hasRealName — это тот же адрес с
  // меткой времени, вдвое длиннее и ни о чём.
  const named = hasRealName(address, name);
  const title = named
    ? `${name}\n${address}`
    : name.trim() === ""
      ? address
      : `${address}\nна бирже не подписан: показанное там имя — его же адрес с меткой времени`;
  return (
    <span className="inline-flex max-w-full items-center gap-1.5">
      <Link
        to={`/wallet/${encodeURIComponent(address)}`}
        title={title}
        // `num` только у адреса: моноширинный шрифт заведён ради того, чтобы
        // шестнадцатеричные строки читались столбиком, а имени он мешает.
        className={`truncate hover:text-[var(--color-accent)] hover:underline${named ? "" : " num"}`}
      >
        {named ? name : shortId(address)}
      </Link>
      <PolymarketProfile address={address} />
    </span>
  );
}

/**
 * Контрагент: адрес ссылкой на его профиль плюс значок тира.
 *
 * Настоящая ссылка, а не onClick: средняя кнопка мыши, «открыть в новой
 * вкладке» и «копировать адрес» должны работать так же, как везде.
 */
export function Counterparty({
  address,
  tier,
  extra,
  title,
  hideEmptyTier,
}: {
  address: string;
  tier: WalletTier | undefined;
  /** Сколько ещё контрагентов было в этой сделке помимо показанного. */
  extra?: number;
  /** Чем этот контрагент был в сделке, если это не очевидно из места показа. */
  title?: string;
  /** Прятать значок, когда тира нет. Зачем — см. Tier.hideEmpty. */
  hideEmptyTier?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Link
        to={`/wallet/${encodeURIComponent(address)}`}
        title={title ? `${title}\n${address}` : address}
        className="num hover:text-[var(--color-accent)] hover:underline"
      >
        {shortId(address)}
      </Link>
      <Tier tier={tier} hideEmpty={Boolean(hideEmptyTier)} />
      {extra ? (
        // Показывается ОДИН контрагент — самый заметный, — но скрывать
        // остальных молча нельзя: сделка против трёх кошельков и против одного
        // это разные события, и «+2» единственное, что их различает.
        <span className="text-[var(--color-muted)]" title="ещё контрагенты в этой сделке">
          +{extra}
        </span>
      ) : null}
    </span>
  );
}
